import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Event as EventIcon,
  Settings as SettingsIcon,
  SystemUpdateAlt as SystemUpdateAltIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions, roles } from "../../config/permissions";
import {
  getPayCycleVersions,
  getVersionDoc,
  setPayCycle,
  updateVersionDoc,
} from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";
import { dayTimeLabel } from "../../utils/queuePresentation";
import {
  buildVersionPayload,
  VERSION_DOC_ID,
  VERSION_FIELDS,
} from "../../utils/versionForm";
import {
  MAX_START_DAY,
  MIN_START_DAY,
  cyclePeriod,
  normaliseVersions,
  periodRange,
} from "../../utils/payCycle";

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

// Staff accounts moved to their own page (044 D11/D12) — src/pages/Staff/Staff.js
// carries the table, ROLE_OPTIONS and the confirm dialog that used to live here.

const ordinal = (n) => {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${n}${suffix}`;
};

const DAY_OPTIONS = Array.from(
  { length: MAX_START_DAY - MIN_START_DAY + 1 },
  (_, i) => MIN_START_DAY + i
);

/**
 * The pay cycle (spec 037) — the one setting that decides which lessons land in
 * which earnings period, on this dashboard AND on every teacher's Pay Slip.
 *
 * The preview is the point of this card. A start day on its own tells an admin
 * nothing about what their change will do: the running period is left alone, and
 * the gap to the new rule becomes a one-off short bridge period. Showing those
 * three periods before they commit is the difference between a considered change
 * and a surprise on 300 teachers' screens.
 */
const PayCycleCard = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === roles.superAdmin;

  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDay, setStartDay] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const stored = await getPayCycleVersions();
    setVersions(stored);
    const live = normaliseVersions(stored);
    setStartDay(String(live[live.length - 1].startDay));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const live = useMemo(() => normaliseVersions(versions), [versions]);
  const currentDay = live[live.length - 1].startDay;
  const chosen = Number(startDay);
  const changed = Number.isInteger(chosen) && chosen !== currentDay;

  // What saving would actually produce, built from the same module the app uses —
  // never a sentence written by hand, which is how a preview starts lying.
  const preview = useMemo(() => {
    if (!changed) return null;
    const now = new Date();
    const proposed = [...live, { startDay: chosen, effectiveFrom: now.toISOString() }];
    return {
      unchanged: periodRange(cyclePeriod(live, now)),
      bridge: periodRange(cyclePeriod(proposed, now, 1)),
      then: periodRange(cyclePeriod(proposed, now, 2)),
    };
  }, [changed, chosen, live]);

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const result = await setPayCycle(chosen);
      setStatus(
        result?.changed
          ? { severity: "success", text: `Pay periods now start on the ${ordinal(chosen)}. The period running today was left as it was.` }
          : { severity: "info", text: "That is already the cycle — nothing was recorded." }
      );
      await load();
    } catch (e) {
      setStatus({ severity: "error", text: e?.message || "Could not change the pay cycle." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Pay cycle
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Which dates each earnings period covers. This drives the tutor pages here
          <strong> and the Pay Slip every teacher sees in the app</strong> — they read the
          same setting, so they can never show different periods.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Chip
            label={`Periods start on the ${ordinal(currentDay)}`}
            color="primary"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <Chip label={`Running now: ${periodRange(cyclePeriod(live, new Date()))}`} />
        </Box>

        {!isSuperAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only a super admin can change the pay cycle. Everything below is read-only for you.
          </Alert>
        )}

        <TextField
          select
          size="small"
          label="Periods start on the"
          value={startDay}
          onChange={(e) => setStartDay(e.target.value)}
          disabled={!isSuperAdmin || saving}
          sx={{ minWidth: 220 }}
          helperText={`${MIN_START_DAY}–${MAX_START_DAY}. The 29th, 30th and 31st cannot be used — February has no such day, so a period pinned to one would be skipped or doubled every year.`}
        >
          {DAY_OPTIONS.map((day) => (
            <MenuItem key={day} value={String(day)}>
              {ordinal(day)}
            </MenuItem>
          ))}
        </TextField>

        {preview && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              What this will do
            </Typography>
            <Typography variant="body2">
              The period running today (<strong>{preview.unchanged}</strong>) is left exactly
              as it is — nobody's current total moves.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Then one bridge period: <strong>{preview.bridge}</strong>. It is a one-off, and it
              exists so no lesson is counted twice or missed.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              After that, periods run normally: <strong>{preview.then}</strong>, and so on.
            </Typography>
          </Alert>
        )}

        {status && (
          <Alert severity={status.severity} sx={{ mt: 2 }}>
            {status.text}
          </Alert>
        )}

        {isSuperAdmin && (
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={save} disabled={!changed || saving}>
              {saving ? "Saving…" : "Change the pay cycle"}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
          History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Every change is kept. Periods that already ran keep the rule that was in force at
          the time, which is what stops a lesson being counted in two periods at once.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Periods start on</TableCell>
              <TableCell>In force from</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {live.map((v, i) => (
              <TableRow key={`${v.startDay}-${i}`}>
                <TableCell>{ordinal(v.startDay)}</TableCell>
                <TableCell>
                  {v.effectiveFrom
                    ? dayTimeLabel(v.effectiveFrom)
                    : "Since before this was recorded"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};


const Settings = () => {
  const { user } = useAuth();
  const userRole = user?.role || "";
  const canEditVersion = hasPermission(userRole, permissions.EDIT_VERSION);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // ── Version card state ──
  const [versionDoc, setVersionDoc] = useState(null);
  const [versionFields, setVersionFields] = useState({
    iosVersion: "",
    androidVersion: "",
  });
  const [versionErrors, setVersionErrors] = useState({});
  const [versionLoadError, setVersionLoadError] = useState("");
  const [savingVersion, setSavingVersion] = useState(false);

  useEffect(() => {
    if (!canEditVersion) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getVersionDoc();
        if (cancelled) return;
        setVersionDoc(data);
        setVersionFields({
          iosVersion: data.iosVersion || "",
          androidVersion: data.androidVersion || "",
        });
      } catch (e) {
        if (!cancelled)
          setVersionLoadError(e.message || "Failed to load the version doc.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEditVersion]);

  const saveVersion = async () => {
    const { valid, errors, payload } = buildVersionPayload(
      versionDoc,
      versionFields
    );
    setVersionErrors(errors);
    if (!valid) {
      if (Object.keys(errors).length === 0) notify("No changes to save.", "info");
      return;
    }
    setSavingVersion(true);
    try {
      await updateVersionDoc(payload);
      await logUserActivity({
        userId: user?.uid || user?.id || "unknown",
        userName: user?.name || user?.displayName || "Unknown User",
        userEmail: user?.email || "unknown@example.com",
        action: "VERSION_UPDATE",
        entityType: "version",
        entityId: VERSION_DOC_ID,
        entityName: "Force-update versions",
        before: Object.fromEntries(
          Object.keys(payload).map((k) => [k, (versionDoc || {})[k] || ""])
        ),
        after: payload,
        description: `${user?.name || "User"} updated the required app version (${Object.entries(
          payload
        )
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ")})`,
      });
      setVersionDoc((d) => ({ ...(d || {}), ...payload }));
      notify("Required versions saved. The app checks on launch and foreground.");
    } catch (e) {
      notify(e.message || "Failed to save the required versions.", "error");
    } finally {
      setSavingVersion(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Settings
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
          Application Settings
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {canEditVersion && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <SystemUpdateAltIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Force update
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The app blocks itself on launch when its build is older than the
                  version set here. Setting a version higher than the store build
                  locks that platform's users out until they update.
                </Typography>
                {versionLoadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {versionLoadError}
                  </Alert>
                )}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 320 }}
                >
                  {VERSION_FIELDS.map(({ key, label }) => (
                    <TextField
                      key={key}
                      size="small"
                      label={label}
                      value={versionFields[key]}
                      error={!!versionErrors[key]}
                      helperText={versionErrors[key] || ""}
                      disabled={savingVersion || versionDoc === null}
                      onChange={(e) =>
                        setVersionFields((f) => ({ ...f, [key]: e.target.value }))
                      }
                    />
                  ))}
                  <Box>
                    <Button
                      variant="contained"
                      disabled={savingVersion || versionDoc === null}
                      onClick={saveVersion}
                      sx={actionButtonSx}
                    >
                      {savingVersion ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <PayCycleCard />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  System Settings
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Configure application settings and user preferences.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
